import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Database,
  Filter,
  Loader2,
  CheckSquare,
  Square,
  Search,
  X,
  ChevronDown,
  Copy,
  Check,
  Lock,
  Activity,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';

interface QueriesViewProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

export const QueriesView: React.FC<QueriesViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState(true);
  const [topQueries, setTopQueries] = useState<any[]>([]);
  const [activeQueries, setActiveQueries] = useState<any[]>([]);
  const [selectedDatabases, setSelectedDatabases] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeQuerySearch, setActiveQuerySearch] = useState<string>('');
  const [isDbDropdownOpen, setIsDbDropdownOpen] = useState<boolean>(false);
  const [selectedQueryForModal, setSelectedQueryForModal] = useState<any | null>(null);
  const [copiedQuery, setCopiedQuery] = useState(false);

  const handleCopyQuery = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedQuery(true);
    setTimeout(() => setCopiedQuery(false), 2000);
  };

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 192 });

  const updateCoords = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 192),
      });
    }
  }, []);

  const toggleDbDropdown = () => {
    if (!isDbDropdownOpen) {
      updateCoords();
    }
    setIsDbDropdownOpen(!isDbDropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsDbDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', updateCoords);
    window.addEventListener('scroll', updateCoords, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [updateCoords]);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60', 10);
    const activeParams: any = {
      cluster_id: activeCluster,
      clusted_id: activeCluster,
      window_minutes: minutes,
    };
    const topParams: any = {
      cluster_id: activeCluster,
      clusted_id: activeCluster,
    };
    if (clientId) {
      activeParams.client_id = clientId;
      topParams.client_id = clientId;
    }

    try {
      const [topRes, activeRes] = await Promise.allSettled([
        baseapi.get('/db_monitor_dashboard/top_queries', { params: topParams }),
        baseapi.get('/db_monitor_dashboard/active_queries', { params: activeParams }),
      ]);

      if (topRes.status === 'fulfilled') {
        const data = topRes.value.data?.data || (Array.isArray(topRes.value.data) ? topRes.value.data : []);
        setTopQueries(Array.isArray(data) ? data : []);
      } else {
        setTopQueries([]);
      }

      if (activeRes.status === 'fulfilled') {
        const aData = activeRes.value.data?.data || (Array.isArray(activeRes.value.data) ? activeRes.value.data : []);
        setActiveQueries(Array.isArray(aData) ? aData : []);
      } else {
        setActiveQueries([]);
      }
    } catch (err) {
      console.warn('QueriesView fetch error:', err);
      setTopQueries([]);
      setActiveQueries([]);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const availableDatabases = useMemo(() => {
    const set = new Set<string>();
    topQueries.forEach((q) => {
      if (q.database) set.add(q.database);
    });
    return Array.from(set).sort();
  }, [topQueries]);

  const toggleDatabaseSelect = (dbName: string) => {
    if (selectedDatabases.includes(dbName)) {
      setSelectedDatabases(selectedDatabases.filter((d) => d !== dbName));
    } else {
      setSelectedDatabases([...selectedDatabases, dbName]);
    }
  };

  const isAllSelected = selectedDatabases.length === 0 || selectedDatabases.length === availableDatabases.length;

  const toggleAllDatabases = () => {
    if (isAllSelected) {
      setSelectedDatabases([]);
    } else {
      setSelectedDatabases([...availableDatabases]);
    }
  };

  const filteredTopQueries = useMemo(() => {
    return topQueries.filter((q) => {
      const matchesDb = selectedDatabases.length === 0 || selectedDatabases.includes(q.database);
      const matchesSearch = !searchQuery.trim() || (q.query && q.query.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesDb && matchesSearch;
    });
  }, [topQueries, selectedDatabases, searchQuery]);

  const filteredActiveQueries = useMemo(() => {
    if (!activeQuerySearch.trim()) return activeQueries;
    const qLower = activeQuerySearch.toLowerCase().trim();
    return activeQueries.filter((q) =>
      (q.query && q.query.toLowerCase().includes(qLower)) ||
      (q.database && q.database.toLowerCase().includes(qLower)) ||
      (q.username && q.username.toLowerCase().includes(qLower)) ||
      (q.state && q.state.toLowerCase().includes(qLower))
    );
  }, [activeQueries, activeQuerySearch]);

  const buttonLabel = useMemo(() => {
    if (selectedDatabases.length === 0 || selectedDatabases.length === availableDatabases.length) {
      return 'All Databases';
    }
    if (selectedDatabases.length === 1) {
      return selectedDatabases[0];
    }
    return `${selectedDatabases.length} Selected`;
  }, [selectedDatabases, availableDatabases]);

  const formatDuration = (sec: number | undefined | null) => {
    if (sec === undefined || sec === null || isNaN(sec)) return '0s';
    if (sec >= 3600) {
      const h = Math.floor(sec / 3600);
      const m = Math.floor((sec % 3600) / 60);
      return `${h}h ${m}m`;
    }
    if (sec >= 60) {
      const m = Math.floor(sec / 60);
      const s = Math.floor(sec % 60);
      return `${m}m ${s}s`;
    }
    return `${sec.toFixed(1)}s`;
  };

  const formatTimestamp = (ts: string | undefined | null) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return ts;
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return ts;
    }
  };

  const getStateBadge = (state: string | undefined) => {
    const s = (state || 'running').toLowerCase();
    if (s === 'running') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
          Running
        </span>
      );
    }
    if (s === 'suspended' || s === 'blocked' || s === 'waiting') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-500/20 text-amber-400 border border-amber-500/30">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
          {state}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-500/20 text-blue-400 border border-blue-500/30">
        {state || 'Active'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl min-h-[350px]">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {/* Top Queries by Cumulative Execution Time */}
      <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3 border-b border-gray-700/50 pb-2.5">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded bg-orange-500 text-white shadow-md">
              <Lock className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-sm font-semibold text-white">Top Queries by Cumulative Execution Time</h3>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Search by Query */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search query text..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-gray-900/90 border border-gray-700 rounded-lg pl-8 pr-7 py-1.5 text-xs text-white placeholder-gray-400 focus:outline-none focus:border-orange-500/50 w-56 h-8"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Database Multi-Select Dropdown */}
            <div className="flex items-center gap-1.5">
              <Filter className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs text-gray-400 font-medium">Database:</span>
              <button
                ref={buttonRef}
                type="button"
                onClick={toggleDbDropdown}
                className="flex items-center justify-between gap-2 bg-gray-900/90 hover:bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-xs font-semibold text-white cursor-pointer transition-colors w-48 shadow-sm h-8"
              >
                <span className="truncate">{buttonLabel}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform duration-200 ${isDbDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDbDropdownOpen &&
                createPortal(
                  <div
                    ref={dropdownRef}
                    style={{
                      position: 'absolute',
                      top: `${coords.top}px`,
                      left: `${coords.left}px`,
                      width: `${coords.width}px`,
                      zIndex: 99999,
                    }}
                    className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl overflow-hidden py-1.5 max-h-60 overflow-y-auto custom-scroll animate-in fade-in slide-in-from-top-1 duration-150"
                  >
                    {/* Select All Option */}
                    <button
                      type="button"
                      onClick={toggleAllDatabases}
                      className="w-full text-left px-3 py-1.5 text-xs font-semibold flex items-center gap-2 cursor-pointer transition-colors hover:bg-gray-800/80 group border-b border-gray-800 pb-1.5 mb-1"
                    >
                      <div className="flex items-center justify-center shrink-0">
                        {isAllSelected ? (
                          <CheckSquare size={16} className="text-orange-400 fill-orange-500/10" />
                        ) : (
                          <Square size={16} className="text-gray-700 group-hover:text-gray-200" />
                        )}
                      </div>
                      <span className="text-gray-200 font-bold">All Databases</span>
                    </button>

                    {/* Available Databases List */}
                    {availableDatabases.map((dbName) => {
                      const isSelected = selectedDatabases.includes(dbName);
                      return (
                        <button
                          key={dbName}
                          type="button"
                          onClick={() => toggleDatabaseSelect(dbName)}
                          className="w-full text-left px-3 py-1.5 text-xs font-semibold flex items-center gap-2 cursor-pointer transition-colors hover:bg-gray-800/80 group"
                        >
                          <div className="flex items-center justify-center shrink-0">
                            {isSelected || isAllSelected ? (
                              <CheckSquare size={16} className="text-orange-400 fill-orange-500/10" />
                            ) : (
                              <Square size={16} className="text-gray-700 group-hover:text-gray-200" />
                            )}
                          </div>
                          <span className="text-gray-200 truncate">{dbName}</span>
                        </button>
                      );
                    })}
                  </div>,
                  document.body
                )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto overflow-y-auto custom-scroll max-h-80 border border-gray-700/50 rounded-lg">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-700/80 bg-gray-900 shadow-sm text-[11px] text-gray-400 uppercase tracking-wider">
                <th className="py-2.5 px-3 w-28 bg-gray-900">Database</th>
                <th className="py-2.5 px-3 w-24 bg-gray-900">User</th>
                <th className="py-2.5 px-3 bg-gray-900">Query Text</th>
                <th className="py-2.5 px-3 text-right w-24 bg-gray-900">Calls</th>
                <th className="py-2.5 px-3 text-right w-28 bg-gray-900">Avg Time</th>
                <th className="py-2.5 px-3 text-right w-32 bg-gray-900">Total Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30 text-gray-200 bg-gray-900/40">
              {filteredTopQueries && filteredTopQueries.length > 0 ? (
                filteredTopQueries.map((q, idx) => {
                  const avg = q.avg_time_ms || 0;
                  const avgStr = avg > 1000 ? `${(avg / 1000).toFixed(2)}s` : `${avg.toFixed(1)}ms`;
                  const total = q.total_time_ms || 0;
                  const totalStr = total > 1000 ? `${(total / 1000).toFixed(2)}s` : `${total.toFixed(0)}ms`;

                  return (
                    <tr
                      key={idx}
                      onClick={() => setSelectedQueryForModal(q)}
                      className="hover:bg-gray-800/70 cursor-pointer transition-colors group"
                      title="Click to view full query details"
                    >
                      <td className="py-2.5 px-3 font-mono text-gray-400">{q.database || '-'}</td>
                      <td className="py-2.5 px-3 font-mono text-gray-400 truncate max-w-[100px]" title={q.username || ''}>
                        {q.username || '-'}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-blue-300 max-w-lg truncate group-hover:text-blue-200 transition-colors" title="Click to view full query">
                        <div className="flex items-center gap-1.5 truncate">
                          <span className="truncate">{q.query}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-300">{(q.calls || 0).toLocaleString()}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-200 font-semibold">{avgStr}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-blue-400 font-bold">{totalStr}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-400 italic">
                    {searchQuery
                      ? `No queries matching "${searchQuery}".`
                      : selectedDatabases.length > 0
                        ? 'No queries found for selected databases.'
                        : 'No top query statistics available.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Active Queries Table */}
      <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3 border-b border-gray-700/50 pb-2.5">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded bg-emerald-500 text-white shadow-md">
              <Activity className="w-4 h-4 text-white" />
            </div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-white">Active Queries</h3>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-mono">
                {filteredActiveQueries.length} {filteredActiveQueries.length === 1 ? 'query' : 'queries'}
              </span>
            </div>
          </div>

          {/* Search Active Queries */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search active queries..."
              value={activeQuerySearch}
              onChange={(e) => setActiveQuerySearch(e.target.value)}
              className="bg-gray-900/90 border border-gray-700 rounded-lg pl-8 pr-7 py-1.5 text-xs text-white placeholder-gray-400 focus:outline-none focus:border-emerald-500/50 w-56 h-8"
            />
            {activeQuerySearch && (
              <button
                onClick={() => setActiveQuerySearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto overflow-y-auto custom-scroll max-h-80 border border-gray-700/50 rounded-lg">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-700/80 bg-gray-900 shadow-sm text-[11px] text-gray-400 uppercase tracking-wider">
                <th className="py-2.5 px-3 w-28 bg-gray-900">Database</th>
                <th className="py-2.5 px-3 w-28 bg-gray-900">User</th>
                <th className="py-2.5 px-3 bg-gray-900">Query Text</th>
                <th className="py-2.5 px-3 w-24 bg-gray-900">State</th>
                <th className="py-2.5 px-3 text-right w-24 bg-gray-900">Duration</th>
                <th className="py-2.5 px-3 text-right w-28 bg-gray-900">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30 text-gray-200 bg-gray-900/40">
              {filteredActiveQueries && filteredActiveQueries.length > 0 ? (
                filteredActiveQueries.map((q, idx) => (
                  <tr
                    key={idx}
                    onClick={() => setSelectedQueryForModal(q)}
                    className="hover:bg-gray-800/70 cursor-pointer transition-colors group"
                    title="Click to view full query details"
                  >
                    <td className="py-2.5 px-3 font-mono text-gray-400">{q.database || '-'}</td>
                    <td className="py-2.5 px-3 font-mono text-gray-400 truncate max-w-[120px]" title={q.username || ''}>
                      {q.username || '-'}
                    </td>
                    <td className="py-2.5 px-3 font-mono text-blue-300 max-w-lg truncate group-hover:text-blue-200 transition-colors" title="Click to view full query">
                      <div className="flex items-center gap-1.5 truncate">
                        <span className="truncate">{q.query}</span>
                      </div>
                    </td>
                    <td className="py-2.5 px-3 whitespace-nowrap">
                      {getStateBadge(q.state)}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-emerald-400 font-bold">
                      {formatDuration(q.duration_sec)}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                      {formatTimestamp(q.collected_at || q.log_timestamp)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-400 italic">
                    {activeQuerySearch
                      ? `No active queries matching "${activeQuerySearch}".`
                      : 'No currently running queries detected.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Full Query Modal Popup */}
      {selectedQueryForModal &&
        createPortal(
          <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[85vh]">
              {/* Modal Header */}
              <div className="px-5 py-3.5 border-b border-gray-800 flex items-center justify-between bg-gray-900/90">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded bg-orange-500/20 text-orange-400">
                    <Database className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">Query Details</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[11px] text-gray-400">
                        DB: <span className="text-gray-200 font-mono">{selectedQueryForModal.database || '-'}</span>
                      </span>
                      <span className="text-gray-600">•</span>
                      <span className="text-[11px] text-gray-400">
                        User: <span className="text-gray-200 font-mono">{selectedQueryForModal.username || '-'}</span>
                      </span>
                      {selectedQueryForModal.hostname && (
                        <>
                          <span className="text-gray-600">•</span>
                          <span className="text-[11px] text-gray-400">
                            Host: <span className="text-gray-200 font-mono">{selectedQueryForModal.hostname}</span>
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setSelectedQueryForModal(null)}
                  className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Modal Metrics Summary Cards */}
              <div className="grid grid-cols-3 gap-3 p-4 bg-gray-950/40 border-b border-gray-800">
                {selectedQueryForModal.calls !== undefined ? (
                  <>
                    <div className="bg-gray-800/40 p-2.5 rounded-lg border border-gray-700/50">
                      <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Calls</div>
                      <div className="text-sm font-bold text-white font-mono mt-0.5">
                        {(selectedQueryForModal.calls || 0).toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-gray-800/40 p-2.5 rounded-lg border border-gray-700/50">
                      <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Avg Execution Time</div>
                      <div className="text-sm font-bold text-gray-200 font-mono mt-0.5">
                        {selectedQueryForModal.avg_time_ms > 1000
                          ? `${(selectedQueryForModal.avg_time_ms / 1000).toFixed(2)}s`
                          : `${(selectedQueryForModal.avg_time_ms || 0).toFixed(1)}ms`}
                      </div>
                    </div>
                    <div className="bg-gray-800/40 p-2.5 rounded-lg border border-gray-700/50">
                      <div className="text-[10px] uppercase tracking-wider text-blue-400 font-semibold">Total Execution Time</div>
                      <div className="text-sm font-bold text-blue-400 font-mono mt-0.5">
                        {selectedQueryForModal.total_time_ms > 1000
                          ? `${(selectedQueryForModal.total_time_ms / 1000).toFixed(2)}s`
                          : `${(selectedQueryForModal.total_time_ms || 0).toFixed(0)}ms`}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-gray-800/40 p-2.5 rounded-lg border border-gray-700/50">
                      <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">State</div>
                      <div className="mt-1">
                        {getStateBadge(selectedQueryForModal.state)}
                      </div>
                    </div>
                    <div className="bg-gray-800/40 p-2.5 rounded-lg border border-gray-700/50">
                      <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Duration</div>
                      <div className="text-sm font-bold text-emerald-400 font-mono mt-0.5">
                        {formatDuration(selectedQueryForModal.duration_sec)}
                      </div>
                    </div>
                    <div className="bg-gray-800/40 p-2.5 rounded-lg border border-gray-700/50">
                      <div className="text-[10px] uppercase tracking-wider text-blue-400 font-semibold">Collected At</div>
                      <div className="text-sm font-bold text-blue-400 font-mono mt-0.5">
                        {formatTimestamp(selectedQueryForModal.collected_at || selectedQueryForModal.log_timestamp)}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Modal Query Body */}
              <div className="p-4 flex-1 overflow-y-auto custom-scroll space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Full Query Text</span>
                  <button
                    onClick={() => handleCopyQuery(selectedQueryForModal.query)}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-md border border-gray-700 transition-colors font-medium cursor-pointer"
                  >
                    {copiedQuery ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="text-emerald-400">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5 text-gray-400" />
                        <span>Copy Query</span>
                      </>
                    )}
                  </button>
                </div>
                <pre className="bg-gray-950 p-3.5 rounded-lg border border-gray-800 text-xs text-blue-300 font-mono whitespace-pre-wrap break-words leading-relaxed max-h-80 overflow-y-auto custom-scroll select-all">
                  {selectedQueryForModal.query || '-'}
                </pre>
              </div>

              {/* Modal Footer */}
              <div className="px-5 py-3 border-t border-gray-800 bg-gray-900/90 flex justify-end">
                <button
                  onClick={() => setSelectedQueryForModal(null)}
                  className="px-4 py-1.5 bg-gray-800 hover:bg-gray-700 text-xs text-white font-medium rounded-lg border border-gray-700 transition-colors cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default QueriesView;
